import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FactoryHp, initialMiningFactoryState, MiningFactoryState, MiningFactory, WeatherForecast } from './state'

export const miningFactorySlice = createSlice({
  name: 'miningFactory',
  initialState: initialMiningFactoryState as MiningFactoryState,
  reducers: {
    setLoading: (state: MiningFactoryState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setLoadingFactoryDetails: (state: MiningFactoryState, action: PayloadAction<boolean>) => {
      state.isLoadingFactoryDetails = action.payload
    },
    setError: (state: MiningFactoryState, action: PayloadAction<string | undefined>) => {
      state.error = action.payload
    },
    setFactoryLevels: (state: MiningFactoryState, action: PayloadAction<Record<number, number>>) => {
      state.factoryLevels = action.payload
    },
    setFactoryHp: (state: MiningFactoryState, action: PayloadAction<Record<number, FactoryHp>>) => {
      state.factoryHp = action.payload
    },
    setWeatherForecastUnlocked: (state: MiningFactoryState, action: PayloadAction<boolean>) => {
      state.isWeatherForecastUnlocked = action.payload
    },
    updateFactoryLevel: (state: MiningFactoryState, action: PayloadAction<{ factoryType: number; level: number }>) => {
      state.factoryLevels[action.payload.factoryType] = action.payload.level
    },
    updateFactoryHp: (state: MiningFactoryState, action: PayloadAction<{ factoryType: number; hp: FactoryHp }>) => {
      state.factoryHp[action.payload.factoryType] = action.payload.hp
    },
    setFactoryRepaired: (state: MiningFactoryState, action: PayloadAction<{ factoryType: number }>) => {
      // After repair, restore HP to max
      const factoryType = action.payload.factoryType
      if (state.factoryHp[factoryType]) {
        state.factoryHp[factoryType].current = state.factoryHp[factoryType].max
      }
      // Also update full factory object if it exists
      if (factoryType > 0 && state.factories[factoryType]) {
        const factory = state.factories[factoryType]!
        factory.hp.current = factory.hp.max
      } else if (factoryType === 0 && state.weatherForecast) {
        state.weatherForecast.hp.current = state.weatherForecast.hp.max
      }
    },
    setFactories: (state: MiningFactoryState, action: PayloadAction<MiningFactory[]>) => {
      // Store complete factory objects indexed by factoryType
      action.payload.forEach((factory) => {
        const factoryType = factory.factoryType
        if (factoryType >= 1 && factoryType <= 4) {
          state.factories[factoryType] = factory
          // Update legacy fields for backward compatibility
          state.factoryLevels[factoryType] = factory.level
          state.factoryHp[factoryType] = factory.hp
        }
      })
    },
    setFactory: (state: MiningFactoryState, action: PayloadAction<{ factoryType: number; factory: MiningFactory | null }>) => {
      const { factoryType, factory } = action.payload
      if (factoryType >= 1 && factoryType <= 4) {
        state.factories[factoryType] = factory
        if (factory) {
          // Update legacy fields for backward compatibility
          state.factoryLevels[factoryType] = factory.level
          state.factoryHp[factoryType] = factory.hp
        }
      }
    },
    setWeatherForecast: (state: MiningFactoryState, action: PayloadAction<WeatherForecast | null>) => {
      state.weatherForecast = action.payload
      if (action.payload) {
        // Update legacy fields for backward compatibility
        state.isWeatherForecastUnlocked = action.payload.unlocked
        state.factoryLevels[0] = action.payload.level
        state.factoryHp[0] = action.payload.hp
      } else {
        state.isWeatherForecastUnlocked = false
        state.factoryLevels[0] = 0
        state.factoryHp[0] = { current: 0, max: 100 }
      }
    },
    updateFactory: (state: MiningFactoryState, action: PayloadAction<{ factoryType: number; updates: Partial<MiningFactory> }>) => {
      const { factoryType, updates } = action.payload
      if (factoryType >= 1 && factoryType <= 4 && state.factories[factoryType]) {
        state.factories[factoryType] = {
          ...state.factories[factoryType]!,
          ...updates,
        }
        // Update legacy fields if level or HP changed
        if (updates.level !== undefined) {
          state.factoryLevels[factoryType] = updates.level
        }
        if (updates.hp) {
          state.factoryHp[factoryType] = updates.hp
        }
        // Also update factory details if it exists
        const factory = state.factories[factoryType]!
        if (factory._id && state.factoryDetails[factory._id]) {
          state.factoryDetails[factory._id] = factory
        }
      }
    },
    setFactoryDetails: (state: MiningFactoryState, action: PayloadAction<{ factoryId: string; factory: MiningFactory | null }>) => {
      const { factoryId, factory } = action.payload
      state.factoryDetails[factoryId] = factory
      // Also update factories by type if factory exists
      if (factory && factory.factoryType >= 1 && factory.factoryType <= 4) {
        state.factories[factory.factoryType] = factory
        state.factoryLevels[factory.factoryType] = factory.level
        state.factoryHp[factory.factoryType] = factory.hp
      }
    },
    setSelectedFactoryId: (state: MiningFactoryState, action: PayloadAction<string | null>) => {
      state.selectedFactoryId = action.payload
    },
    clearError: (state: MiningFactoryState) => {
      state.error = undefined
    },
  }
})

export const {
  setLoading,
  setLoadingFactoryDetails,
  setError,
  setFactoryLevels,
  setFactoryHp,
  setWeatherForecastUnlocked,
  updateFactoryLevel,
  updateFactoryHp,
  setFactoryRepaired,
  setFactories,
  setFactory,
  setWeatherForecast,
  updateFactory,
  setFactoryDetails,
  setSelectedFactoryId,
  clearError,
} = miningFactorySlice.actions

export default miningFactorySlice.reducer

